import { Container, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { MAPS } from '@/config/maps';
import type { MoteStyle } from '@/config/themes';

interface Mote {
  sprite: Sprite;
  vx: number;
  vy: number;
  phase: number;
  baseAlpha: number;
}

/** Hash → [0, 1) for mote seeding (visual only — no gameplay rng) */
function unit(i: number, salt: number): number {
  const v = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return v - Math.floor(v);
}

/** Longest frame step a mote integrates (tab switches, hitches) */
const MAX_DT = 0.05;

/**
 * Ambient particles of the sector theme (docs/04): a FIXED pool of sprites
 * drifting around the camera view and wrapping inside it — constant cost,
 * no allocation after construction. Visual only (render-time clock).
 */
export class AmbientMotes {
  readonly view = new Container();
  private readonly motes: Mote[] = [];
  private style: MoteStyle | null = null;
  private lastT = 0;

  constructor() {
    for (let i = 0; i < MAPS.motes.count; i++) {
      const sprite = new Sprite(getTexture('env/mote'));
      sprite.anchor.set(0.5);
      sprite.blendMode = 'add';
      this.view.addChild(sprite);
      this.motes.push({ sprite, vx: 0, vy: 0, phase: 0, baseAlpha: 1 });
    }
    this.view.visible = false;
  }

  /** New room: restyle every mote and scatter it over the view around (cx, cy). */
  setStyle(style: MoteStyle, cx: number, cy: number, viewW: number, viewH: number): void {
    this.style = style;
    const M = MAPS.motes;
    const tex = getTexture('env/mote', style.frame);
    const w = Math.max(viewW, 1) + M.margin * 2;
    const h = Math.max(viewH, 1) + M.margin * 2;
    this.motes.forEach((m, i) => {
      m.sprite.texture = tex;
      m.sprite.tint = style.tint;
      m.sprite.scale.set(M.scale[0] + unit(i, 1) * (M.scale[1] - M.scale[0]));
      m.sprite.position.set(cx - w / 2 + unit(i, 2) * w, cy - h / 2 + unit(i, 3) * h);
      m.vx = style.vx[0] + unit(i, 4) * (style.vx[1] - style.vx[0]);
      m.vy = style.vy[0] + unit(i, 5) * (style.vy[1] - style.vy[0]);
      m.phase = unit(i, 6) * Math.PI * 2;
      m.baseAlpha = M.alpha[0] + unit(i, 7) * (M.alpha[1] - M.alpha[0]);
    });
    this.view.visible = true;
  }

  /** Drift + wrap inside the view band around the camera centre. */
  render(cx: number, cy: number, viewW: number, viewH: number, timeS: number): void {
    const style = this.style;
    if (!style) return;
    const dt = Math.min(MAX_DT, Math.max(0, timeS - this.lastT));
    this.lastT = timeS;
    const M = MAPS.motes;
    const hw = viewW / 2 + M.margin;
    const hh = viewH / 2 + M.margin;
    for (const m of this.motes) {
      const s = m.sprite;
      let x = s.x + m.vx * dt;
      let y = s.y + m.vy * dt;
      if (x < cx - hw) x += hw * 2; else if (x > cx + hw) x -= hw * 2;
      if (y < cy - hh) y += hh * 2; else if (y > cy + hh) y -= hh * 2;
      s.position.set(x, y);
      s.alpha = m.baseAlpha * (0.55 + 0.45 * Math.sin(timeS * style.twinkle + m.phase));
      s.rotation += style.spin * dt;
    }
  }
}
